import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AxiosResponse } from 'axios'
import { isMobile } from 'react-device-detect'
import { toast } from 'react-toastify'
import casinoService from '../../services/casino.service'
import authService from '../../services/auth.service'
import { useGameOverlayHistory } from '../../hooks/useGameOverlayHistory'
import { resolveCasinoGameImageUrl } from '../../utils/lotusCasinoImagePreload'
import { resolveMac88RemoteImageUrl } from '../../utils/mac88LocalImage'
import {
  PK_LC_NETWIN_FALLBACK,
  PkNetwinRow,
  getGameSearchText,
  maskWinnerName,
} from './playKaroLiveCasinoData'
import type { PkCasinoCatalogConfig, PkCatalogProvider } from './playKaroCasinoCatalogTypes'
import {
  getCachedCatalogGames,
  subscribeCatalogGamesLoad,
  type CatalogFetchMode,
} from './playKaroCasinoCatalogLoader'
import './PlayKaroLiveCasino.css'

const PAGE_SIZE = 24

function catalogParam(name: string): string | null {
  if (typeof window === 'undefined') return null
  return new URLSearchParams(window.location.search).get(name)
}

function scrollChipIntoCenter(scroller: HTMLElement, chip: HTMLElement) {
  const scrollerRect = scroller.getBoundingClientRect()
  const chipRect = chip.getBoundingClientRect()
  const delta = chipRect.left + chipRect.width / 2 - (scrollerRect.left + scrollerRect.width / 2)
  scroller.scrollTo({ left: Math.max(0, scroller.scrollLeft + delta), behavior: 'smooth' })
}
const API_BASE = (process.env.REACT_APP_API_BASEURL || '').replace(/\/api\/?$/, '')

type PlayKaroCasinoCatalogProps = {
  config: PkCasinoCatalogConfig
  preLoginMode?: boolean
  onLoginRequired?: () => void
  hideHero?: boolean
  hideProviders?: boolean
  loadMoreMode?: 'auto' | 'click'
  searchPlaceholder?: string
  loadMoreLabel?: string
}

function resolveImageSources(
  game: Record<string, unknown>,
  overrideImages?: Record<string, string>,
  useCustomImage?: Record<string, boolean>,
): { primary: string; fallback: string } {
  const gid = String(game.game_identifier || game.identifier || game._id || '')
  if (gid && overrideImages?.[gid] && useCustomImage?.[gid]) {
    const override = overrideImages[gid]
    const primary = override.startsWith('http') ? override : `${API_BASE}${override}`
    return { primary, fallback: '' }
  }
  const resolved = resolveCasinoGameImageUrl(game)
  const remote = resolveMac88RemoteImageUrl(game)
  const webp = typeof game.game_images_webp === 'string' ? game.game_images_webp.trim() : ''
  if (resolved.startsWith('/imgs/mac88/') && remote && remote !== resolved) {
    return { primary: resolved, fallback: remote }
  }
  if (resolved) return { primary: resolved, fallback: webp && webp !== resolved ? webp : remote !== resolved ? remote : '' }
  return { primary: remote || webp, fallback: webp && webp !== remote ? webp : '' }
}

function providerLabel(game: Record<string, unknown>): string {
  return String(game.game_provider || game.provider || game.provider_name || '').trim()
}

function compactId(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '')
}

function useAutoIndex(length: number, delay: number, paused = false) {
  const [index, setIndex] = useState(0)
  useEffect(() => {
    if (paused || length <= 1) return undefined
    const id = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % length)
    }, delay)
    return () => window.clearInterval(id)
  }, [length, delay, paused])
  return [index, setIndex] as const
}

function matchesCategory(
  game: Record<string, unknown>,
  id: string,
  index: number,
  categories: PkCasinoCatalogConfig['categories'],
): boolean {
  if (id === 'all') return true
  if (id === 'new') return index < 16
  if (id === 'featured' || id === 'top_rated') return index < 24
  const found = categories.find((c) => c.id === id)
  return found?.rx ? found.rx.test(getGameSearchText(game)) : true
}

function matchesProvider(
  game: Record<string, unknown>,
  id: string,
  providers: readonly PkCatalogProvider[],
): boolean {
  if (id === 'all') return true
  const found = providers.find((p) => p.id === id)
  const text = getGameSearchText(game)
  if (found?.rx) return found.rx.test(text)
  const compact = compactId
  return compact(text).includes(id) || compact(String(game.game_provider || '')) === id || compact(String(game.provider || '')) === id
}

function useHorizontalDragScroll<T extends HTMLElement>() {
  const ref = useRef<T | null>(null)
  const dragged = useRef(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return undefined
    let isDown = false
    let startX = 0
    let startScroll = 0

    const onDown = (e: PointerEvent) => {
      const iosTouch =
        e.pointerType !== 'mouse' &&
        typeof CSS !== 'undefined' &&
        CSS.supports('-webkit-touch-callout', 'none')
      if (iosTouch) return
      if (e.pointerType === 'mouse' && e.button !== 0) return
      isDown = true
      dragged.current = false
      startX = e.clientX
      startScroll = el.scrollLeft
      el.classList.add('is-dragging')
    }
    const onMove = (e: PointerEvent) => {
      if (!isDown) return
      const dx = e.clientX - startX
      if (Math.abs(dx) > 4) dragged.current = true
      el.scrollLeft = startScroll - dx
    }
    const onUp = () => {
      if (!isDown) return
      isDown = false
      el.classList.remove('is-dragging')
    }

    el.addEventListener('pointerdown', onDown)
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    return () => {
      el.removeEventListener('pointerdown', onDown)
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
    }
  }, [])

  const consumeDrag = () => {
    if (!dragged.current) return false
    dragged.current = false
    return true
  }

  return { ref, consumeDrag }
}

function PkGameTile({
  game,
  onPlay,
  overrideImages,
  useCustomImage,
}: {
  game: Record<string, unknown>
  onPlay: (g: Record<string, unknown>) => void
  overrideImages?: Record<string, string>
  useCustomImage?: Record<string, boolean>
}) {
  const { primary, fallback } = resolveImageSources(game, overrideImages, useCustomImage)
  const [src, setSrc] = useState(primary)
  const [failed, setFailed] = useState(false)
  const name = String(game.game_name || 'Game')

  useEffect(() => {
    setSrc(primary)
    setFailed(false)
  }, [primary, game])

  const handleError = () => {
    if (fallback && src !== fallback) {
      setSrc(fallback)
      return
    }
    setFailed(true)
  }

  return (
    <button type="button" className="pk-lc__game" onClick={() => onPlay(game)} title={name}>
      {src && !failed ? (
        <img
          className="pk-lc__game-bg"
          src={src}
          alt=""
          loading="lazy"
          decoding="async"
          onError={handleError}
        />
      ) : (
        <span className="pk-lc__game-bg" />
      )}
      <span className="pk-lc__brand">{providerLabel(game) || 'LIVE'}</span>
      <span className="pk-lc__play">PLAY REAL</span>
      <span className="pk-lc__gname">{name}</span>
    </button>
  )
}

function PkProvButton({
  label,
  icon,
  active,
  onPick,
}: {
  label: string
  icon?: string
  active: boolean
  onPick: () => void
}) {
  const [iconFailed, setIconFailed] = useState(false)
  const showText = !icon || iconFailed

  return (
    <button
      type="button"
      className={`pk-lc__prov-btn${showText ? ' is-text' : ''}${active ? ' is-on' : ''}`}
      onClick={onPick}
      aria-label={label}
    >
      {icon && !iconFailed ? (
        <img className="pk-lc__prov-ico" src={icon} alt="" onError={() => setIconFailed(true)} />
      ) : null}
      <span className="pk-lc__prov-name">{label}</span>
    </button>
  )
}

function RevealCell({ children }: { children: React.ReactNode }) {
  return <div className="pk-lc__cell">{children}</div>
}

function PkGameSkeleton() {
  return (
    <div className="pk-lc__game is-skeleton" aria-hidden="true">
      <span className="pk-lc__game-bg" />
    </div>
  )
}

function PkCatalogSkeletonGrid({ count = 12 }: { count?: number }) {
  return (
    <div className="pk-lc__grid is-full is-loading">
      {Array.from({ length: count }, (_, i) => (
        <RevealCell key={`sk-${i}`}>
          <PkGameSkeleton />
        </RevealCell>
      ))}
    </div>
  )
}

const PlayKaroCasinoCatalog: React.FC<PlayKaroCasinoCatalogProps> = ({
  config,
  preLoginMode,
  onLoginRequired,
  hideHero = false,
  hideProviders = false,
  loadMoreMode = 'auto',
  searchPlaceholder = 'Search games here...',
  loadMoreLabel = 'Load More Games ⌄',
}) => {
  const [games, setGames] = useState<Record<string, unknown>[]>([])
  const [wins, setWins] = useState<PkNetwinRow[]>(PK_LC_NETWIN_FALLBACK)
  const [loading, setLoading] = useState(true)
  const [query, setQuery] = useState('')
  const [searchParams] = useSearchParams()
  const focusCat = searchParams.get('cat')
  const focusProv = searchParams.get('prov')
  const [category, setCategory] = useState(() => {
    const id = catalogParam('cat')
    if (id && config.categories.some((item) => item.id === id)) return id
    return config.categories[0]?.id || 'all'
  })
  const [provider, setProvider] = useState(() => {
    const id = catalogParam('prov')
    if (id && config.seedProviders.some((item) => item.id === id)) return id
    return 'all'
  })
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)
  const [overrideImages, setOverrideImages] = useState<Record<string, string>>({})
  const [useCustomImage, setUseCustomImage] = useState<Record<string, boolean>>({})
  const [gamePlay, setGamePlay] = useState<Record<string, unknown> | undefined>()
  const [gameUrl, setGameUrl] = useState('')
  const [showIframe, setShowIframe] = useState(false)

  const closeCasinoGame = useCallback(() => {
    setGamePlay(undefined)
    setGameUrl('')
    setShowIframe(false)
  }, [])

  const closeGameOverlay = useGameOverlayHistory(!!gamePlay && !preLoginMode, closeCasinoGame)

  useEffect(() => {
    if (gamePlay) {
      document.body.classList.add('casino-game-playing')
      window.scrollTo(0, 0)
    } else {
      document.body.classList.remove('casino-game-playing')
    }
    return () => document.body.classList.remove('casino-game-playing')
  }, [gamePlay])

  useEffect(() => {
    if (!gamePlay) setShowIframe(false)
  }, [gamePlay])

  useEffect(() => {
    if (!gameUrl) return
    const t = setTimeout(() => setShowIframe(true), 120)
    return () => clearTimeout(t)
  }, [gameUrl])

  useEffect(() => {
    if (preLoginMode || !gamePlay) return
    setGameUrl('')
    const identifier = gamePlay.identifier || gamePlay.game_identifier
    const payload = {
      gameCode: identifier,
      lobby_url: identifier,
      ipAddress: authService.getIpAddress(),
      isMobile,
    }
    casinoService
      .getplaycasino(payload)
      .then((res: AxiosResponse<any>) => {
        const data = res?.data
        const raw =
          data?.data?.url ??
          data?.data?.playUrl ??
          data?.data?.play_url ??
          data?.data?.gameUrl ??
          data?.url ??
          data?.playUrl
        const url = raw && typeof raw === 'string' ? raw.trim() : null
        if (url) {
          setGameUrl(url)
        } else {
          toast.error('Game could not be loaded. Please try again.')
          setGamePlay(undefined)
        }
      })
      .catch((error: any) => {
        if (error.response?.status === 401) {
          toast.error('Please login to play casino games')
        } else {
          toast.error('Failed to load game. Please try again.')
        }
        setGamePlay(undefined)
      })
  }, [gamePlay, preLoginMode])

  const [bannerPaused, setBannerPaused] = useState(false)
  const [bannerDrag, setBannerDrag] = useState(0)
  const [bannerDragging, setBannerDragging] = useState(false)
  const bannerStart = useRef<{ x: number; index: number } | null>(null)
  const [bannerIndex, setBannerIndex] = useAutoIndex(config.banners.length, 3000, bannerPaused)
  const leftWins = wins.slice(0, Math.ceil(wins.length / 2))
  const rightWins = wins.slice(Math.ceil(wins.length / 2))
  const leftPages = Math.max(1, leftWins.length - 2)
  const rightPages = Math.max(1, rightWins.length - 2)
  const [leftIndex] = useAutoIndex(leftPages, 3000)
  const [rightIndex] = useAutoIndex(rightPages, 3000)
  const catsDrag = useHorizontalDragScroll<HTMLUListElement>()
  const provDrag = useHorizontalDragScroll<HTMLDivElement>()

  useEffect(() => {
    if (!focusCat) return
    if (config.categories.some((item) => item.id === focusCat)) setCategory(focusCat)
  }, [focusCat, config.categories])

  useEffect(() => {
    if (!focusProv) return
    if (config.seedProviders.some((item) => item.id === focusProv)) setProvider(focusProv)
  }, [focusProv, config.seedProviders])

  const showRecommended = category === 'all' && provider === 'all' && !query.trim()

  const catalogProviders = useMemo(() => {
    const theirs = config.seedProviders.filter((prov) => {
      if (prov.id === 'all') return true
      return games.some((game) => matchesProvider(game, prov.id, config.seedProviders))
    })
    const seen = new Set<string>(theirs.map((p) => p.id))
    const extras: PkCatalogProvider[] = []
    for (const game of games) {
      const label = providerLabel(game)
      if (!label) continue
      const id = compactId(label)
      if (!id || seen.has(id)) continue
      if (!config.showAllFoundProviders && config.extraProviderAllow && !config.extraProviderAllow.has(id)) {
        continue
      }
      const alreadyListed = theirs.some((p) => p.rx?.test(getGameSearchText(game)))
      if (alreadyListed) continue
      seen.add(id)
      extras.push({
        id,
        label: label.toUpperCase(),
        icon: config.resolveProviderIcon?.(id, label),
      })
    }
    return [...theirs, ...extras]
  }, [games, config])

  useEffect(() => {
    if (category === 'all' && provider === 'all') return
    const centerActive = () => {
      if (category !== 'all' && catsDrag.ref.current) {
        const chip = catsDrag.ref.current.querySelector('.pk-lc__cat.is-on')
        if (chip instanceof HTMLElement) scrollChipIntoCenter(catsDrag.ref.current, chip)
      }
      if (provider !== 'all' && provDrag.ref.current) {
        const chip = provDrag.ref.current.querySelector('.pk-lc__prov.is-on')
        if (chip instanceof HTMLElement) scrollChipIntoCenter(provDrag.ref.current, chip)
      }
    }
    const frame = window.requestAnimationFrame(centerActive)
    const timer = window.setTimeout(centerActive, 400)
    return () => {
      window.cancelAnimationFrame(frame)
      window.clearTimeout(timer)
    }
  }, [category, provider, catalogProviders, catsDrag.ref, provDrag.ref])

  const searchCasinoGamesApi = useCallback(
    (term: string) =>
      preLoginMode ? casinoService.searchPublicCasinoGames(term) : casinoService.searchCasinoGames(term),
    [preLoginMode],
  )

  const fetchIntCasinoList = useCallback(
    (prov: string, type: string) =>
      preLoginMode
        ? casinoService.getPublicIntCasinoList(prov, type)
        : casinoService.getIntCasinoList(prov, type),
    [preLoginMode],
  )

  useEffect(() => {
    let cancelled = false
    const schedule =
      typeof window !== 'undefined' && 'requestIdleCallback' in window
        ? (fn: () => void) => {
            const id = window.requestIdleCallback(fn, { timeout: 2500 })
            return () => window.cancelIdleCallback(id)
          }
        : (fn: () => void) => {
            const id = window.setTimeout(fn, 400)
            return () => window.clearTimeout(id)
          }

    const cancelSchedule = schedule(() => {
      if (cancelled) return
      const req = preLoginMode
        ? casinoService.getPublicCasinoVisibilityConfig()
        : casinoService.getCasinoVisibilityConfig()
      req
        .then((res: AxiosResponse<any>) => {
          if (cancelled) return
          const data = res.data?.data || res.data || {}
          setOverrideImages(data.overrideImages || {})
          setUseCustomImage(data.useCustomImage || {})
        })
        .catch(() => {
          if (!cancelled) {
            setOverrideImages({})
            setUseCustomImage({})
          }
        })
    })

    return () => {
      cancelled = true
      cancelSchedule()
    }
  }, [preLoginMode])

  useEffect(() => {
    let cancelled = false
    let rafId = 0
    let pendingGames: Record<string, unknown>[] | null = null
    const mode: CatalogFetchMode = preLoginMode ? 'guest' : 'auth'
    const cached = getCachedCatalogGames(config.catalogId, mode)

    if (cached?.length) {
      setGames(cached)
      setLoading(false)
    } else {
      setGames([])
      setLoading(true)
    }

    const flushProgress = () => {
      rafId = 0
      if (cancelled || pendingGames === null) return
      setGames(pendingGames)
      setLoading(false)
      pendingGames = null
    }

    const onProgress = (allGames: Record<string, unknown>[]) => {
      if (cancelled) return
      pendingGames = allGames
      if (!rafId) {
        rafId = window.requestAnimationFrame(flushProgress)
      }
    }

    const { promise, unsubscribe } = subscribeCatalogGamesLoad(
      config,
      mode,
      {
        searchApi: (term) => searchCasinoGamesApi(term).then((res) => res.data?.result || []),
        listApi: (provider, type) =>
          fetchIntCasinoList(provider, type).then((res) => res.data?.result || []),
      },
      onProgress,
    )

    void promise
      .then((finalGames) => {
        if (cancelled) return
        if (rafId) {
          window.cancelAnimationFrame(rafId)
          rafId = 0
        }
        setGames(finalGames)
      })
      .catch(() => {
        if (!cancelled) setGames([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
      unsubscribe()
      if (rafId) window.cancelAnimationFrame(rafId)
    }
  }, [searchCasinoGamesApi, fetchIntCasinoList, config, preLoginMode])

  useEffect(() => {
    if (preLoginMode) {
      setWins(PK_LC_NETWIN_FALLBACK)
      return undefined
    }
    let cancelled = false
    const schedule =
      typeof window !== 'undefined' && 'requestIdleCallback' in window
        ? (fn: () => void) => {
            const id = window.requestIdleCallback(fn, { timeout: 3000 })
            return () => window.cancelIdleCallback(id)
          }
        : (fn: () => void) => {
            const id = window.setTimeout(fn, 500)
            return () => window.clearTimeout(id)
          }

    const loadWins = async () => {
      try {
        const res = await casinoService.getCasinoBetHistory({ page: 1, limit: 40 })
        const rows = (res as { data?: { data?: unknown[] } }).data?.data
        if (!Array.isArray(rows) || rows.length === 0) return
        const mapped: PkNetwinRow[] = rows
          .filter((row) => Number((row as { winAmount?: number }).winAmount) > 0)
          .slice(0, 20)
          .map((row, i) => {
            const r = row as Record<string, unknown>
            return {
              user: maskWinnerName(String(r.username || r.userName || r.user || 'USR')),
              amount: Number(r.winAmount || r.pnl || 0).toFixed(2),
              game: String(r.gameName || r.game_name || r.game || 'Slots'),
              when: 'a day ago',
              image: PK_LC_NETWIN_FALLBACK[i % PK_LC_NETWIN_FALLBACK.length].image,
            }
          })
        if (!cancelled && mapped.length > 0) setWins(mapped)
      } catch {
        if (!cancelled) setWins(PK_LC_NETWIN_FALLBACK)
      }
    }

    const cancelSchedule = schedule(() => {
      if (cancelled) return
      void loadWins()
    })

    return () => {
      cancelled = true
      cancelSchedule()
    }
  }, [preLoginMode])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return games.filter((game, index) => {
      if (!matchesCategory(game, category, index, config.categories)) return false
      if (!matchesProvider(game, provider, catalogProviders)) return false
      if (q && !getGameSearchText(game).toLowerCase().includes(q)) return false
      return true
    })
  }, [games, category, provider, query, config.categories, catalogProviders])

  const recommended = useMemo(() => filtered.slice(0, 12), [filtered])
  const [recIndex, setRecIndex] = useAutoIndex(Math.max(recommended.length, 1), 2500)
  const shown = filtered.slice(0, visibleCount)
  const topEight = showRecommended ? shown.slice(0, 8) : []
  const restGrid = showRecommended ? shown.slice(8) : shown
  const moreRef = useRef<HTMLDivElement>(null)
  const hasMore = visibleCount < filtered.length

  useEffect(() => {
    setVisibleCount(PAGE_SIZE)
  }, [category, provider, query])

  useEffect(() => {
    if (loadMoreMode !== 'auto') return undefined
    const el = moreRef.current
    if (!el || !hasMore) return undefined
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisibleCount((n) => n + PAGE_SIZE)
        }
      },
      { root: null, rootMargin: '280px 0px', threshold: 0 },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [hasMore, visibleCount, loadMoreMode])

  const onPlay = (game: Record<string, unknown>) => {
    if (preLoginMode) {
      onLoginRequired?.()
      return
    }
    setGamePlay(game)
  }

  const renderWin = (row: PkNetwinRow, key: string) => (
    <div key={key} className="pk-lc__win">
      <div className="pk-lc__win-row">
        <div className="pk-lc__win-img">
          <span>
            <img src={row.image} alt="" />
          </span>
        </div>
        <div className="pk-lc__win-detail">
          <div className="pk-lc__win-top">
            <span className="pk-lc__win-user">{row.user}</span>
            <span className="pk-lc__win-amt">
              <span className="pk-lc__win-ccy">INR</span>
              <span className="pk-lc__win-num">{row.amount}</span>
            </span>
          </div>
          <div className="pk-lc__win-bot">
            <span className="pk-lc__win-game">{row.game}</span>
            <span className="pk-lc__win-when">{row.when}</span>
          </div>
        </div>
      </div>
    </div>
  )

  const tileProps = { onPlay, overrideImages, useCustomImage }

  const onBannerPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return
    bannerStart.current = { x: event.clientX, index: bannerIndex }
    setBannerPaused(true)
    setBannerDragging(true)
    setBannerDrag(0)
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const onBannerPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!bannerStart.current) return
    setBannerDrag(event.clientX - bannerStart.current.x)
  }

  const onBannerPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!bannerStart.current) return
    const dx = event.clientX - bannerStart.current.x
    const width = event.currentTarget.offsetWidth || 1
    const total = config.banners.length
    if (total > 1 && dx < -width * 0.18) {
      setBannerIndex((bannerStart.current.index + 1) % total)
    } else if (total > 1 && dx > width * 0.18) {
      setBannerIndex((bannerStart.current.index - 1 + total) % total)
    }
    bannerStart.current = null
    setBannerDrag(0)
    setBannerDragging(false)
    setBannerPaused(false)
  }

  const bannerTrackStyle = {
    transform: `translate3d(calc(${-bannerIndex * 100}% + ${bannerDrag}px), 0, 0)`,
    transition: bannerDragging ? 'none' : 'transform 0.35s ease',
  }

  return (
    <div className="pk-lc">
      {!hideHero ? (
      <div className="pk-lc__hero">
        <div className="pk-lc__hero-col">
          <div className="pk-lc__poster-wrap">
            <div
              className={`pk-lc__poster${bannerDragging ? ' is-dragging' : ''}`}
              onPointerDown={onBannerPointerDown}
              onPointerMove={onBannerPointerMove}
              onPointerUp={onBannerPointerUp}
              onPointerCancel={onBannerPointerUp}
            >
              <div className="pk-lc__poster-track" style={bannerTrackStyle}>
                {config.banners.map((banner, i) => (
                  <div key={banner.src} className={`pk-lc__poster-slide${i === bannerIndex ? ' is-on' : ''}`}>
                    <img src={banner.src} alt={banner.alt} draggable={false} />
                  </div>
                ))}
              </div>
              <div className="pk-lc__dots">
                {config.banners.map((banner, i) => (
                  <button
                    key={banner.src}
                    type="button"
                    className={`pk-lc__dot${i === bannerIndex ? ' is-on' : ''}`}
                    onClick={() => setBannerIndex(i)}
                    aria-label={`Banner ${i + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="pk-lc__hero-col">
          <div className="pk-lc__netwin">
            <div className="pk-lc__netwin-inner">
              <div className="pk-lc__netwin-col is-left">
                <div className="pk-lc__netwin-track" style={{ transform: `translateY(-${leftIndex * 33.333}%)` }}>
                  {leftWins.map((row, i) => renderWin(row, `l-${i}`))}
                </div>
              </div>
              <div className="pk-lc__netwin-col is-right">
                <div className="pk-lc__netwin-track" style={{ transform: `translateY(-${rightIndex * 33.333}%)` }}>
                  {rightWins.map((row, i) => renderWin(row, `r-${i}`))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      ) : null}

      <div className="pk-lc__toolbar">
        <ul className="pk-lc__cats" ref={catsDrag.ref}>
          {config.categories.map((cat) => (
            <li key={cat.id}>
              <button
                type="button"
                className={`pk-lc__cat${category === cat.id ? ' is-on' : ''}`}
                onClick={() => {
                  if (catsDrag.consumeDrag()) return
                  setCategory(cat.id)
                }}
              >
                {cat.label}
              </button>
            </li>
          ))}
        </ul>
        <div className={`pk-lc__search${query.trim() ? ' is-filled' : ''}`}>
          <input
            type="search"
            placeholder={searchPlaceholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoComplete="off"
          />
        </div>
      </div>

      {!hideProviders ? (
      <div className="pk-lc__providers" ref={provDrag.ref}>
        {catalogProviders.map((prov) => (
          <div key={prov.id} className={`pk-lc__prov${provider === prov.id ? ' is-on' : ''}`}>
            <PkProvButton
              label={prov.label}
              icon={prov.icon}
              active={provider === prov.id}
              onPick={() => {
                if (provDrag.consumeDrag()) return
                setProvider(prov.id)
              }}
            />
          </div>
        ))}
      </div>
      ) : null}

      {loading && games.length === 0 ? (
        <>
          <PkCatalogSkeletonGrid count={12} />
          <div className="pk-lc__loading" aria-live="polite">
            {config.loadingLabel}
          </div>
        </>
      ) : null}

      {!loading && games.length === 0 ? (
        <div className="pk-lc__empty">{config.emptyLabel}</div>
      ) : null}

      {showRecommended && recommended.length > 0 ? (
        <div className="pk-lc__rec-row">
          <div className="pk-lc__feat">
            <div className="pk-lc__feat-ring">
              <div className="pk-lc__feat-box">
                <div className="pk-lc__feat-blur" aria-hidden="true">
                  {recommended.map((game, i) => {
                    const src = resolveImageSources(game, overrideImages, useCustomImage).primary
                    return src ? (
                      <img
                        key={String(game._id || game.identifier || `blur-${i}`)}
                        src={src}
                        alt=""
                        className={i === recIndex ? 'is-on' : ''}
                      />
                    ) : null
                  })}
                </div>
                <h2>
                  <strong>Recommended Games</strong>
                </h2>
                <div className="pk-lc__feat-slider">
                  <button
                    type="button"
                    className="pk-lc__nav is-prev"
                    onClick={() => setRecIndex((prev) => (prev - 1 + recommended.length) % recommended.length)}
                    aria-label="Previous slide"
                  >
                    ‹
                  </button>
                  <div className="pk-lc__feat-card">
                    {recommended.map((game, i) => (
                      <div
                        key={String(game._id || game.identifier || i)}
                        className={`pk-lc__feat-slide${i === recIndex ? ' is-on' : ''}`}
                      >
                        <PkGameTile game={game} {...tileProps} />
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="pk-lc__nav is-next"
                    onClick={() => setRecIndex((prev) => (prev + 1) % recommended.length)}
                    aria-label="Next slide"
                  >
                    ›
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="pk-lc__top8">
            {topEight.map((game, i) => (
              <RevealCell key={String(game._id || game.identifier || `top-${i}`)}>
                <PkGameTile game={game} {...tileProps} />
              </RevealCell>
            ))}
          </div>
        </div>
      ) : null}

      {games.length > 0 || !loading ? (
      <div className="pk-lc__grid is-full">
        {restGrid.map((game, i) => (
          <RevealCell key={String(game._id || game.identifier || `g-${i}`)}>
            <PkGameTile game={game} {...tileProps} />
          </RevealCell>
        ))}
        {!loading && shown.length === 0 ? <div className="pk-lc__empty">{config.emptyLabel}</div> : null}
        {loading && games.length > 0 ? (
          Array.from({ length: 4 }, (_, i) => (
            <RevealCell key={`tail-sk-${i}`}>
              <PkGameSkeleton />
            </RevealCell>
          ))
        ) : null}
      </div>
      ) : null}

      {hasMore ? (
        <div className="pk-lc__more-wrap" ref={moreRef}>
          <button type="button" className="pk-lc__more" onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}>
            {loadMoreLabel}
          </button>
        </div>
      ) : null}
      {!preLoginMode && gamePlay ? (
        <div className="slot-iframe show">
          <div
            className="slot-header"
            style={{
              background: '#0b1a1d',
              padding: '0 15px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              color: '#fff',
              height: 44,
              minHeight: 44,
              borderBottom: '1px solid #1a2f33',
            }}
          >
            <h4 className="mb-0" style={{ fontSize: '20px', fontWeight: 400 }}>
              {String(gamePlay.game_name || gamePlay.name || 'Game')}
            </h4>
            <div className="close-slot-frame" onClick={closeGameOverlay} style={{ cursor: 'pointer', padding: '8px 16px', background: '#1a2f33', borderRadius: '4px' }}>
              EXIT
            </div>
          </div>
          {gameUrl ? (
            showIframe ? (
              <iframe
                src={gameUrl}
                title={String(gamePlay.game_name || gamePlay.name || 'Game')}
                allow="autoplay; fullscreen; clipboard-read; clipboard-write"
                allowFullScreen
                style={{ width: '100%', border: 'none' }}
              />
            ) : (
              <div className="slot-iframe-loading">
                <div className="lds-hourglass" />
              </div>
            )
          ) : (
            <div className="slot-iframe-loading">
              <div className="lds-hourglass" />
            </div>
          )}
        </div>
      ) : null}
    </div>
  )
}

export default PlayKaroCasinoCatalog
